import React, { useState, useEffect, useContext } from 'react';
import { Modal } from 'react-bootstrap';
import ActionButtons from './field_inputs/shared/ActionButtons';
import PropTypes from 'prop-types';
import { DataContext as SharedDataContext } from '../../../stores/crm-store';
import EditSelectPipelineStage from './field_inputs/pipeline/stage/EditSelectPipelineStage';
import SpinLoader from './field_inputs/shared/SpinLoader';
import OnHoverTooltip from '../../tables/shared/OnHoverTooltip';
import { useTranslation } from 'react-i18next';
import { GENERAL_ERROR_MESSAGE, GENERAL_ERROR_CONTACT_MESSAGE } from '../../../i18n/keyConstants';
/**
 * "+ Deal", "+ Person", "+ Organization", etc.
 * Generic button that opens modal window for user to enter data in order to create object.
 */
const GenericFormModal = ({
    label,
    description,
    defaultFormValues,
    formComponents,
    handleAdd,
    onSuccessiveSave,
    onValueSet,
    buttonType,
    disabled = false,
    disabledTooltipText,
    callback,
    isExl
}) => {

    buttonType = buttonType ? buttonType : 'btn-success';

    // ------------------------ States
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formValues, setFormValues] = useState(defaultFormValues ?? {});
    const [errorMessages, setErrorMessages] = useState({});
    const { t } = useTranslation(['errorMessages']);

    const setFormValue = (propName, value) => {
        const newValues = { ...formValues };
        newValues[propName] = value;
        formComponents.forEach((c) => {
            if (!c.bindOnChange) return;
            const boundChange = c.bindOnChange(newValues, propName);
            if (boundChange) {
                newValues[c.propName] = boundChange;
            }
        });

        // If user changes pipeline -> also update piplineStage and selectable stages.
        if (propName === 'pipeline') {
            formComponents.forEach((fc) => {
                if (fc.component.type === EditSelectPipelineStage) {
                    fc.component = React.cloneElement(fc.component, {
                        stages: value.pipelineStages,
                    });
                }
            });
        }
        if (onValueSet) {
            onValueSet(propName, newValues[propName]);
        }
        setFormValues(newValues);
    };
    const setErrorMessage = (propName, e) => {
        errorMessages[propName] = e;
        setErrorMessages({ ...errorMessages });
    };

    useEffect(() => {
        setFormValues(defaultFormValues ?? {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultFormValues]);

    useEffect(() => {
        if (callback) {
            callback(formValues)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues]);

    const { store, dispatch } = useContext(SharedDataContext);

    // ------------------------ Methods
    const renderModalFormComponent = (component, formValues, setFormValue, errorMessages) => {
        const errorMessage = errorMessages[component.propName];
        const isInvalid = errorMessage && errorMessage.length;
        return (
            <div key={component.propName}>
                {component.visible !== false ?
                    <div>
                        {component.label ? <label>{component.label}</label> : <div style={{ paddingTop: '.3rem' }} />}
                        {React.cloneElement(component.component, {
                            isInvalid,
                            value: formValues[component.propName],
                            setValue: (v) => setFormValue(component.propName, v),
                        })}
                        {isInvalid && <div className='crm-invalid-msg'>{errorMessage}</div>}
                    </div> : null}
            </div>
        );
    };

    const clearForm = () => {
        setFormValues({ ...defaultFormValues });
        setErrorMessages({});
    };

    const onSave = async () => {
        if (isSaving) return;
        setIsSaving(true);

        const item = { ...formValues };

        // E.g let item.pipelineId = item.pipeline.id
        Object.keys(item).forEach((propName) => {
            if (item[propName]?.id !== undefined) {
                item[propName + 'Id'] = item[propName].id;
            }
        });

        let isInvalid = false;
        formComponents.forEach((c) => {
            if (c.validate) {
                const msg = c.validate(item[c.propName], item);
                setErrorMessage(c.propName, msg);
                if (msg && msg.length) {
                    isInvalid = true;
                }
            }
        });

        if (isInvalid) {
            setIsSaving(false);
            return;
        }

        const response = await handleAdd(item);
        setIsSaving(false);
        if (response.success) {
            await new Promise((r) => setTimeout(r, 200));
            clearForm();
            setIsModalOpen(false);
            dispatch({ type: 'SHOW_SAVED_TOAST' });

            if (onSuccessiveSave) {
                onSuccessiveSave(response);
            }
        } else {
            dispatch({ type: 'SHOW_ERROR_TOAST' });
            alert(`${t(GENERAL_ERROR_MESSAGE)}:\n${response.error ?? response.message ?? t(GENERAL_ERROR_CONTACT_MESSAGE)}`);
        }
    };

    const onCancel = () => {
        clearForm();
        setIsModalOpen(false);
    };

    // ------- Component
    if (!store.areSelectOptionsFetched && !isExl) {
        return <LoadingAddButton phantomLabel={label} />;
    }

    const toggleButton = (
        <button
            type='button'
            disabled={disabled}
            className={`btn ${buttonType} mx-1 crm-modal-btn justify-content-center align-content-center`}
            onClick={() => setIsModalOpen(true)}>
            <div>{label}</div>
        </button>
    );

    return (
        <>
            {disabledTooltipText ? (
                <OnHoverTooltip
                    labelClassNames='d-flex margin-none'
                    dataFor='show-won-button'
                    tooltipType='dark'
                    tooltipPlace='left'
                    tooltipEffect='solid'>
                    {toggleButton}
                    <span className='d-flex my-auto'>{disabledTooltipText}</span>
                </OnHoverTooltip>
            ) : (
                toggleButton
            )}
            <Modal show={isModalOpen} onHide={onCancel} className='crm-add-item-modal'>
                <div className='d-flex justify-content-between align-items-center padding-top-20px padding-left-20px padding-right-20px'>
                    <h4 className='margin-none'>{description}</h4>
                    <button type='button' className='btn btn-secondary crm-modal-btn ' onClick={onCancel}>
                        &times;
                    </button>
                </div>
                <div className='add-item-form'>
                    <Modal.Body>
                        {formComponents &&
                            formComponents.map((c) => {
                                return renderModalFormComponent(c, formValues, setFormValue, errorMessages);
                            })}
                    </Modal.Body>
                    <Modal.Footer>
                        <ActionButtons {...{ onCancel, onSave, isSaving }} />
                    </Modal.Footer>
                </div>
            </Modal>
        </>
    );
};

GenericFormModal.propTypes = {
    /** Label for the button, e.g. "+ Deal" */
    label: PropTypes.string.isRequired,
    /** Title of modal, e.g. "Lägg till ny Deal" */
    description: PropTypes.string.isRequired,
    /** Optional default formvalues, e.g for non-clearable select. */
    defaultFormValues: PropTypes.any,
    /** Form components that takes user input and sets formValues. */
    formComponents: PropTypes.arrayOf(
        PropTypes.shape({
            propName: PropTypes.string.isRequired,
            label: PropTypes.string,
            visible: PropTypes.bool,
            validate: PropTypes.function,
            component: PropTypes.element,
        })
    ).isRequired,
    /** Async function to save item. Returns {success: bool, string?: message} */
    handleAdd: PropTypes.func.isRequired,
};

export default GenericFormModal;

/** A button of same size but with loader instead of the label. */
const LoadingAddButton = ({ phantomLabel }) => {
    return (
        <button type='button' className='btn btn-success mr-1 crm-modal-btn justify-content-center align-content-center crm-loader-wrapper'>
            <div className='invisible'>{phantomLabel}</div>
            <SpinLoader isLoading />
        </button>
    );
};
